import { useBehavior, useTriggerAction } from '@libs/hooks';
import { ActionActionTypeEnum, TButtonData, TMargin, TWidget } from '@libs/types';
import clsx from 'clsx';
import React, { Activity, ElementType, MouseEventHandler } from 'react';
import { TRowActionData } from '../../types/data';
import { TRowActionOption } from '../../types/options';
import { getInlineStyles } from '../../utils/styles';
import { Visual } from '../Image/Visual';
import { TextCommon } from '../UI/Text/TextCommon';
import styles from './styles/rowAction.module.css';

type RowWidgetProps = (
    | {
          isWidget: true;
          widget: TWidget<TRowActionData, TRowActionOption>;
      }
    | {
          isWidget: false;
          handleClick: MouseEventHandler;
          iconName?: string;
          title?: string;
          subtitle?: string;
          helperText?: string;
          subtitleTextPosition?: TRowActionOption['subtitleTextPosition'];
          margin?: TMargin;
      }
) & {
    as?: ElementType;
};

export const RowAction = (props: RowWidgetProps) => {
    const { isWidget, as } = props;
    const finalWidget = isWidget
        ? props.widget
        : ({
              uid: '',
              options: {
                  iconName: props.iconName,
                  margin: props.margin,
                  subtitleTextPosition: props.subtitleTextPosition,
              },
              data: {
                  iconName: props.iconName,
                  messages: {
                      title: props.title,
                      subtitle: props.subtitle,
                      helperText: props.helperText,
                  },
              },
          } as TWidget<TRowActionData, TRowActionOption>);

    const { triggerActionsByType } = useTriggerAction(finalWidget);
    const { isDisabled, isHidden } = useBehavior(finalWidget as TWidget<TButtonData>);

    const title = finalWidget.data?.messages?.title;
    const subtitle = finalWidget.data?.messages?.subtitle;
    const helperText = finalWidget.data?.messages?.helperText;
    const copiedText = finalWidget.data?.messages?.copiedText;

    const isSubtitleTextPositionTop = (finalWidget.options?.subtitleTextPosition ?? 'top') === 'top';
    const iconName = finalWidget.options?.iconName ?? finalWidget.data?.iconName;
    const RowActionComponent = as ?? 'button';

    const onClickHandler = () => {
        triggerActionsByType(ActionActionTypeEnum.onClick, {
            playerActionOptions: {
                textForCopyToClipboard: title,
                toastMessage: copiedText,
            },
        }).then();
    };

    return (
        <Activity mode={isHidden ? 'hidden' : 'visible'}>
            <RowActionComponent
                className={clsx(styles.container, isDisabled && styles.disabled)}
                data-test={finalWidget.uid}
                aria-disabled={isDisabled}
                aria-label={finalWidget.data?.messages?.title}
                aria-hidden={isHidden}
                style={getInlineStyles({ margin: finalWidget.options?.margin })}
                onClick={onClickHandler}
            >
                <div className={styles.textContent}>
                    <div className={styles.topTextContent}>
                        {isSubtitleTextPositionTop && subtitle && (
                            <div data-test={'subtitle-' + finalWidget.uid} className={styles.subtitle}>
                                <TextCommon isWidget={false} content={subtitle} options={{ variant: 'secondary' }} />
                            </div>
                        )}
                        {title && <TextCommon uid={'title-' + finalWidget.uid} isWidget={false} content={title} options={{ variant: 'main' }} />}
                        {!isSubtitleTextPositionTop && subtitle && (
                            <div data-test={'subtitle-' + finalWidget.uid} className={styles.subtitle}>
                                <TextCommon isWidget={false} content={subtitle} options={{ variant: 'secondary' }} />
                            </div>
                        )}
                    </div>
                    {isSubtitleTextPositionTop && helperText && (
                        <div data-test={'helperText-' + finalWidget.uid} className={styles.subtitle}>
                            <TextCommon isWidget={false} content={helperText} options={{ variant: 'secondary' }} />
                        </div>
                    )}
                </div>
                {iconName && (
                    <div className={styles.icon}>
                        <Visual isWidget={false} dataTest={`icon-${finalWidget.uid}`} iconName={iconName} size={'small'} isDisabled={isDisabled} />
                    </div>
                )}
            </RowActionComponent>
        </Activity>
    );
};
